import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Target, X } from "lucide-react";

import DashboardLayout from "@/components/layout/DashboardLayout";
import LoadingSpinner from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import Typography from "@/components/ui/typography";
import {
  getStarterProgress,
  LevelsKeys,
  StarterLicenseeProgress,
} from "@/features/founders/api/levels";
import UpdateProgressionDialog from "@/features/founders/components/UpdateProgressionDialog";

const CertifiedPush = () => {
  const [progressionFounder, setProgressionFounder] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const {
    data: starterFounders = [],
    isLoading,
    error,
  } = useQuery<StarterLicenseeProgress[]>({
    queryKey: [LevelsKeys.GET_STARTER_PROGRESS],
    queryFn: getStarterProgress,
  });

  const renderCheckIcon = (completed: boolean) => {
    return completed ? (
      <Check className="h-5 w-5 text-green-600" />
    ) : (
      <X className="h-5 w-5 text-gray-400" />
    );
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Typography size="h1">Certified Push</Typography>
          <div className="text-red-600">
            Error loading starter progress: {(error as Error).message}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Typography size="h1">Certified Push</Typography>
            <p className="text-sm text-muted-foreground mt-1">
              Tracking founders working toward Certified level
            </p>
          </div>
          <div className="text-sm text-muted-foreground">
            {starterFounders.length} founder
            {starterFounders.length !== 1 ? "s" : ""}
          </div>
        </div>

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-center">Niche Defined</TableHead>
                <TableHead className="text-center">Course Completed</TableHead>
                <TableHead className="text-center">Demo Published</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {starterFounders.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No founders are currently working toward Certified level
                  </TableCell>
                </TableRow>
              ) : (
                starterFounders.map((founder) => (
                  <TableRow key={founder.id}>
                    <TableCell className="font-medium">
                      {founder.fullName ||
                        `${founder.firstName} ${founder.lastName}`}
                    </TableCell>
                    <TableCell>{founder.email}</TableCell>
                    <TableCell className="text-center">
                      {renderCheckIcon(founder.progress.nicheDefined)}
                    </TableCell>
                    <TableCell className="text-center">
                      {renderCheckIcon(founder.progress.courseCompleted)}
                    </TableCell>
                    <TableCell className="text-center">
                      {renderCheckIcon(founder.progress.demoPublished)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setProgressionFounder({
                            id: founder.id,
                            name:
                              founder.fullName ||
                              `${founder.firstName} ${founder.lastName}`,
                          })
                        }
                        className="gap-1"
                      >
                        <Target className="h-3 w-3" />
                        Update
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <UpdateProgressionDialog
        open={!!progressionFounder}
        onOpenChange={(open) => !open && setProgressionFounder(null)}
        licenseeId={progressionFounder?.id || null}
        licenseeName={progressionFounder?.name || ""}
      />
    </DashboardLayout>
  );
};

export default CertifiedPush;
